/* The parts of a call that are the same whoever rang whom. TEMPLATE-OWNED.
 *
 * There are two pages now - the inbound demo (index.html, you ring the business) and the
 * outbound one (outbound/index.html, the business rings you) - and they share a handset,
 * a waveform, a caption stream and a clock. This is that, and only that.
 *
 * WHAT DELIBERATELY STAYS IN EACH PAGE, because a test pins it there and because getting
 * it wrong is silent: `remoteAudio.srcObject` / `.play()`, and the `callbacks: {}` block.
 * See the audio note below - echo cancellation depends on the bot's voice being played
 * through the <audio> element, and a shared helper that took the element as an argument
 * would make that rule one indirection harder to check.
 *
 * ── audio, and the two rules ────────────────────────────────────────────────────────
 * ONE. Audio is opened IN THE TAP: `openAudioContext()` and `remoteAudio.play()` run
 * before the handler's first await, and `resume()` is never awaited. A mobile browser
 * lets a page start sound only while the tap that asked for it is still live, and two
 * fetches later it is not. Chrome on iOS then leaves the context suspended with a
 * `resume()` that never settles, so an awaited one hangs the call on "calling…" before
 * Daily ever asks for the microphone. An Android in-app browser refuses the late play()
 * instead, and the call connects with a bot nobody can hear.
 *
 * TWO. The bot's voice MUST be played through the <audio> element. That is the path the
 * browser's AEC uses as its reference signal when it subtracts what the speakers are
 * playing from what the microphone hears. Route it through audioCtx.destination instead
 * and Chrome's AEC flatlines: the agent hears itself, transcribes itself, and interrupts
 * itself.
 *
 * `tap()` below is a TAP for the waveform. It is deliberately never connected onward to
 * audioCtx.destination - a MediaStreamSource with nothing downstream still pulls data for
 * getByteFrequencyData, so the visualiser works without making a second, AEC-invisible
 * playback path. The ringer IS connected to the destination, and it is the only thing in
 * the app that is: it is silenced before the bot's track arrives, so it never overlaps the
 * window in which the browser needs a clean reference.
 */

/* ── the wire ──────────────────────────────────────────────────────── */

export async function postJson(url, body) {
  return fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/* ── the token ─────────────────────────────────────────────────────── */

/* A demo link is read, left open in a tab, and pressed much later. The token the call
 * routes want lives ten minutes (functions/api/_auth.js) and that short life is the point:
 * nothing can revoke one once it is minted, and ten minutes is all a leaked one is worth.
 * So a page must mint one a moment before it SPENDS it, rather than on the way in. Minting
 * it at page load is what made an hour-old page answer the call button with "that session
 * expired", and answer the second press with a reload notice.
 *
 * `mint` is the page's own POST to /api/session, passed in rather than written here,
 * because only the page knows what that route wants of it - a slug here, a passcode there -
 * and because a test pins that call, with its arguments, inside the page that makes it.
 *
 * The life of a token is the `expiresIn` the mint reported: a DURATION, counted from the
 * moment the answer landed on this clock. Never the `exp` inside the token itself - that is
 * the server's clock, and the demo server a laptop runs does not even mint the same shape
 * of token. The margin comes off the end so that a call placed on a token's last second is
 * not a call that reaches the edge a second too late.
 */
const TOKEN_MARGIN_MS = 30000;
const TOKEN_FALLBACK_SECONDS = 60;

/**
 * A token, minted on demand and kept only while it is comfortably live.
 *
 * `get()` answers `{ok, token, status}` - the status is the mint's, so each caller can
 * decide for itself what a 401, a 410 or a 404 means from where it is standing. `clear()`
 * throws the held token away, which is what a route that has just refused one wants.
 */
export function createToken(mint, marginMs = TOKEN_MARGIN_MS) {
  let token = null;
  let goodUntil = 0;
  let minting = null;

  async function fetchOne() {
    token = null;
    goodUntil = 0;
    const res = await mint().catch(() => null);
    if (!res) return { ok: false, status: 0 };
    if (!res.ok) return { ok: false, status: res.status };
    const body = await res.json().catch(() => ({}));
    if (!body.token) return { ok: false, status: res.status };
    token = body.token;
    // A mint that forgot to say costs a fresh token per press, which is the safe way to be
    // wrong: the call still goes through.
    const seconds = Number(body.expiresIn) > 0 ? Number(body.expiresIn) : TOKEN_FALLBACK_SECONDS;
    goodUntil = Date.now() + seconds * 1000;
    return { ok: true, status: res.status, token };
  }

  return {
    get() {
      if (token && Date.now() < goodUntil - marginMs) {
        return Promise.resolve({ ok: true, status: 200, token });
      }
      // One mint for two presses in the same second.
      if (!minting) minting = fetchOne().finally(() => { minting = null; });
      return minting;
    },
    clear() {
      token = null;
      goodUntil = 0;
    },
  };
}

/* ── the clock ─────────────────────────────────────────────────────── */

/** Seconds as mm:ss. Everything that shows a call length shows it this way. */
export function clock(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

/* ── the ring ────────────────────────────────────────────────────────
 * Two cadences, because the two pages are two different events.
 *
 * `ringback` is what YOU hear while a phone you dialled rings somewhere else: the
 * Australian pattern, 400 Hz modulated at 25 Hz, 0.4s on / 0.2s off / 0.4s on / 2.0s off.
 * The disc's sonar rings run the same 3s cycle in CSS, so what you hear and what you see
 * are one event.
 *
 * `incoming` is what you hear when a phone rings AT you: brisker, higher, three bursts in
 * a shorter cycle. It is the only audible signal on the outbound page that the assistant
 * is now on the line and waiting - so it has to be distinguishable from the other one
 * within about a second.
 */

const PATTERNS = {
  ringback: { carrier: 400, modulation: 25, cycle: 3.0, level: 0.14,
              bursts: [[0, 0.4], [0.6, 1.0]] },
  incoming: { carrier: 520, modulation: 18, cycle: 2.2, level: 0.16,
              bursts: [[0, 0.35], [0.5, 0.85], [1.0, 1.35]] },
};

export function createRinger() {
  let ctx = null;
  let nodes = null;
  let timer = null;

  function start(audioCtx, name = "ringback") {
    if (!audioCtx || nodes) return;
    const pattern = PATTERNS[name] || PATTERNS.ringback;
    ctx = audioCtx;

    const cadence = ctx.createGain();
    cadence.gain.value = 0;
    cadence.connect(ctx.destination);

    const am = ctx.createGain();
    am.gain.value = 0.5;
    am.connect(cadence);

    const carrier = ctx.createOscillator();
    carrier.frequency.value = pattern.carrier;
    carrier.connect(am);

    const mod = ctx.createOscillator();
    mod.frequency.value = pattern.modulation;
    const depth = ctx.createGain();
    depth.gain.value = 0.5;
    mod.connect(depth);
    depth.connect(am.gain);

    carrier.start();
    mod.start();
    nodes = { cadence, carrier, mod };

    const cycle = (t0) => {
      const g = cadence.gain;
      g.setValueAtTime(0, t0);
      for (const [on, off] of pattern.bursts) {
        g.setValueAtTime(0, t0 + on);
        g.linearRampToValueAtTime(pattern.level, t0 + on + 0.02);
        g.setValueAtTime(pattern.level, t0 + off - 0.02);
        g.linearRampToValueAtTime(0, t0 + off);
      }
    };
    cycle(ctx.currentTime + 0.05);
    timer = setInterval(() => {
      if (nodes) cycle(ctx.currentTime + 0.05);
    }, pattern.cycle * 1000);
  }

  function stop() {
    clearInterval(timer);
    timer = null;
    if (!nodes) return;
    const { cadence, carrier, mod } = nodes;
    nodes = null;
    const now = ctx.currentTime;
    cadence.gain.cancelScheduledValues(now);
    cadence.gain.setValueAtTime(cadence.gain.value, now);
    cadence.gain.linearRampToValueAtTime(0, now + 0.08);
    setTimeout(() => {
      try { carrier.stop(); mod.stop(); } catch (_) { /* already stopped */ }
    }, 150);
  }

  return { start, stop, get ringing() { return nodes !== null; } };
}

/* ── the waveform ──────────────────────────────────────────────────── */

/**
 * Draw the ring of bars until the returned function is called.
 *
 * `sources()` returns `[agentAnalyser, callerAnalyser]`, read every frame rather than
 * captured once: the analysers are attached as tracks arrive, which is after this starts.
 */
export function startWave(canvas, accent, sources) {
  const ctx2d = canvas.getContext("2d");
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const size = 440;
  canvas.width = size * dpr;
  canvas.height = size * dpr;
  ctx2d.scale(dpr, dpr);

  const BARS = 68;
  // One buffer per source. Sharing a single array meant whichever analyser was read
  // last decided the shape, so the bars always drew the microphone.
  const agentBins = new Uint8Array(256);
  const callerBins = new Uint8Array(256);
  const smoothed = new Float32Array(BARS);
  let rafId = null;

  const read = (analyser, into) => {
    if (!analyser) {
      into.fill(0);
      return 0;
    }
    analyser.getByteFrequencyData(into);
    let sum = 0;
    for (let i = 0; i < 64; i++) sum += into[i];
    return sum / 64 / 255;
  };

  const cx = size / 2;
  const cy = size / 2;
  const base = size * 0.305;

  const draw = () => {
    rafId = requestAnimationFrame(draw);
    ctx2d.clearRect(0, 0, size, size);

    const [agentAnalyser, callerAnalyser] = sources();
    const agent = read(agentAnalyser, agentBins);
    const caller = read(callerAnalyser, callerBins);
    const agentLouder = agent >= caller;
    const bins = agentLouder ? agentBins : callerBins;

    // The brand accent when the agent talks, white when you do.
    ctx2d.strokeStyle = agentLouder ? accent : "#f2f6f8";
    ctx2d.lineCap = "round";
    ctx2d.lineWidth = 9;

    for (let i = 0; i < BARS; i++) {
      const idx = Math.floor((i / BARS) * 56);
      const target = bins[idx] / 255;
      smoothed[i] += (target - smoothed[i]) * 0.3;
      const len = 5 + smoothed[i] * size * 0.15;
      const a = (i / BARS) * Math.PI * 2 - Math.PI / 2;
      const cos = Math.cos(a);
      const sin = Math.sin(a);
      ctx2d.globalAlpha = 0.3 + smoothed[i] * 0.7;
      ctx2d.beginPath();
      ctx2d.moveTo(cx + cos * base, cy + sin * base);
      ctx2d.lineTo(cx + cos * (base + len), cy + sin * (base + len));
      ctx2d.stroke();
    }
    ctx2d.globalAlpha = 1;
  };
  draw();

  return () => {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = null;
  };
}

/** The accent the waveform draws the agent in, read from the brand stylesheet. */
export function accentColour(fallback = "#4fb0ff") {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue("--leaf")
    .trim();
  return value || fallback;
}

/* ── captions ──────────────────────────────────────────────────────── */

/**
 * A factory for caption lines in `node`. Returns `(who, cls) => element`.
 *
 * Older lines fade rather than scroll away, and only the last few are kept: a phone
 * screen is not a transcript, and a wall of text on one reads as a log.
 */
export function createCaptions(node, { keep = 8 } = {}) {
  return (who, cls) => {
    const line = document.createElement("p");
    line.className = `caption ${cls}`;
    line.dataset.who = who;
    node.appendChild(line);
    while (node.children.length > keep) node.firstElementChild.remove();
    for (const child of node.children) child.classList.add("caption-fading");
    line.classList.remove("caption-fading");
    node.scrollTop = node.scrollHeight;
    return line;
  };
}

/* ── the audio context ─────────────────────────────────────────────── */

/**
 * An AudioContext, asked to run, for a page to open inside the tap (rule ONE above).
 *
 * `resume()` is fired and forgotten: where the tap still counts it runs at once, and where
 * it does not, awaiting it is the hang. A browser that cannot make one at all still gets a
 * call - `tap()` and the ringer both take a null context as "no waveform, no ringback".
 */
export function openAudioContext() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    ctx.resume().catch(() => {});
    return ctx;
  } catch (err) {
    console.warn("no audio context; the call goes ahead without ringback or waveform", err);
    return null;
  }
}

/* ── the analyser tap ──────────────────────────────────────────────── */

/**
 * An analyser reading `track`, connected to nothing downstream.
 *
 * Chrome only routes a WebRTC stream into WebAudio when it is also attached to a media
 * element, which is why the caller attaches the bot's track to <audio> as well. Calling
 * getUserMedia again for the microphone would open a SECOND capture stream, which is
 * exactly how AEC gets defeated - so the transport's own track is what comes in here.
 */
export function tap(audioCtx, track) {
  if (!audioCtx) return null;
  const source = audioCtx.createMediaStreamSource(new MediaStream([track]));
  const analyser = audioCtx.createAnalyser();
  analyser.fftSize = 512;
  source.connect(analyser);
  // Returned as a pair so the caller can hold the source: a MediaStreamSource with no
  // live reference is collected mid-call, and the waveform goes flat.
  return { source, analyser };
}

/** Report what the browser actually applied, since constraints are requests. */
export function reportAudioConstraints(client) {
  const track = client?.tracks()?.local?.audio;
  if (!track || typeof track.getSettings !== "function") return;
  const s = track.getSettings();
  if (s.echoCancellation === false) {
    console.warn("Echo cancellation is OFF for this microphone — expect the agent " +
                 "to hear itself. Check chrome://webrtc-internals.", s);
  } else {
    console.info("mic:", {
      echoCancellation: s.echoCancellation,
      noiseSuppression: s.noiseSuppression,
      autoGainControl: s.autoGainControl,
    });
  }
}

/* ── a staged note ─────────────────────────────────────────────────────
 * Both pages have a wait with nothing to show for it - a cold Pipecat Cloud container
 * takes ten to twenty seconds to come up - and a screen that says nothing for that long
 * reads as a hang. One line at four seconds was not enough: it is the stretch after it
 * that feels long. So each page writes a short script, in the order the agent actually
 * boots, and this steps through it.
 */

export function createNotes(node, script) {
  let timers = [];

  function show(text) {
    node.textContent = text;
    node.hidden = false;
    // A line replacing another should fade up the way the first one did. Unhiding runs
    // the animation once; a text swap alone would not.
    node.style.animation = "none";
    void node.offsetWidth;
    node.style.animation = "";
  }

  function clear() {
    for (const id of timers) clearTimeout(id);
    timers = [];
    node.hidden = true;
  }

  /** Arm the script, timed from `t0` (a `performance.now()` reading). */
  function schedule(t0) {
    clear();
    const elapsed = performance.now() - t0;
    for (const [at, text] of script) {
      timers.push(setTimeout(() => show(text), Math.max(0, at * 1000 - elapsed)));
    }
  }

  return { schedule, clear, show };
}
